import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Plane, Luggage, Globe, Shield, Clock, TrendingUp, CreditCard, Award, Coffee, Sparkles, FileText, Star, Medal, Gem, Crown, Check } from 'lucide-react';
import { User } from '@/api/entities';
import { Button } from '@/components/ui/button';
import SectionBand from '@/components/ui/SectionBand';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { useMilesBalanceRealtime } from '@/hooks/useMilesBalanceRealtime';
import { useUserStatus } from '@/hooks/useUserStatus';
import StatusProgressCard from '@/components/status/StatusProgressCard';
import { TIERS, TIER_BENEFITS, TIER_THEME } from '@/constants/tiers';
import {
  BRAND_NESTED_PANEL,
  BRAND_NESTED_ROW,
  BRAND_PRIMARY_BTN_CLASS,
  BRAND_ROYAL_RGB,
  REWARDS_SECTION_CARD,
} from '@/constants/brandColors';
import { cn } from '@/lib/utils';
import { GIFT_CARD_MERCHANTS, isValidGiftCardMiles } from '@/constants/giftCards';
import { fmtCurrency } from '@/utils/money';
import {
  giftCardCentsForMiles,
  MILES_CHECKOUT_MIN,
  MILES_TRIP_BONUS_PERCENT,
  MILES_TRIP_CENTS_PER_MILE,
  tripCentsForMiles,
} from '../../supabase/functions/_shared/miles-redemption.js';
import GiftCardRedeemModal from '@/components/rewards/GiftCardRedeemModal';
import ServiceInquiryCard from '@/components/support/ServiceInquiryCard';
import ShopAndEarnSection from '@/components/rewards/ShopAndEarnSection';
import EarnCreditPromo from '@/components/rewards/EarnCreditPromo';
import { CARD_LINKED_MILES_ENABLED } from '@/utils/cardLinkedMilesFlag';
import PageSeo from '@/components/PageSeo';
import MyVipBadge from '@/components/vip/MyVipBadge';

/** Program guide panels inside Rewards section cards. */
const PROGRAM_GUIDE_SHELL = `${REWARDS_SECTION_CARD} shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]`;

/** Ascending prestige ladder for the tier-benefits grid. */
const TIER_ICON = { member: Star, silver: Medal, gold: Award, platinum: Gem, diamond: Crown };

export default function Rewards() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [calculatorMiles, setCalculatorMiles] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [redeemPrefill, setRedeemPrefill] = useState({ merchant: '', miles: '' });
  const hasSetCalculatorDefault = useRef(false);
  const calculatorRef = useRef(null);

  const calculatorMerchants = GIFT_CARD_MERCHANTS;
  const currentUser = user;
  const { balance: milesBalance, isLoading } = useMilesBalanceRealtime(currentUser?.id ?? null, { isOwnUser: true });
  const { status: userStatus, isLoading: isStatusLoading } = useUserStatus(currentUser?.id ?? null, { isOwnUser: true });

  // Scroll to top when page loads, or to miles calculator if hash or state requests it
  useEffect(() => {
    const shouldScrollToCalculator = location.hash === '#miles-calculator' || location.state?.scrollToCalculator;
    if (shouldScrollToCalculator) {
      const scrollToCalculator = () => {
        const el = calculatorRef.current || document.getElementById('miles-calculator');
        if (el) {
          requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
          return true;
        }
        return false;
      };
      scrollToCalculator();
      const t1 = setTimeout(scrollToCalculator, 100);
      const t2 = setTimeout(scrollToCalculator, 400);
      const t3 = setTimeout(scrollToCalculator, 800);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.state?.scrollToCalculator]);

  // Default calculator to available miles when logged in (once)
  useEffect(() => {
    if (user && !isLoading && milesBalance > 0 && !hasSetCalculatorDefault.current) {
      setCalculatorMiles(String(milesBalance));
      hasSetCalculatorDefault.current = true;
    }
  }, [user, isLoading, milesBalance]);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const u = await User.getCurrentUser();
        setUser(u);
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    };

    loadUserData();
  }, []);

  const inProgress = userStatus?.in_progress;
  const nextTierId = inProgress?.next_tier;
  const tierProgressPercent =
    !inProgress
      ? 0
      : !nextTierId
        ? 100
        : Math.min(100, Math.max(0, inProgress.percent_to_next ?? 0));
  const tierProgressCaption = !inProgress
    ? 'Progress to next tier'
    : !nextTierId
      ? 'Highest tier unlocked'
      : 'Progress to next tier';

  const rewardTiers = [
    { miles: '1,500+', benefit: 'Preferred Seating', description: 'Apply Miles toward additional seats on your booking', icon: Gift, color: 'from-brand-500 to-brand-300', type: 'travel' },
    { miles: '3,500+', benefit: 'Checked Baggage', description: 'Apply Miles toward a checked bag on eligible flights', icon: Luggage, color: 'from-brand-500 to-cyan-500', type: 'travel' },
    { miles: '8,000+', benefit: 'Cabin Class Upgrade', description: 'Apply Miles toward cabin upgrades on eligible flights', icon: Plane, color: 'from-green-500 to-emerald-500', type: 'travel' }
  ];

  const starbucksOption = {
    miles: '500+',
    benefit: 'Gift Card',
    description: 'E-gift cards at 200+ brands, 1¢ per Mile (500+ Miles)',
    icon: Coffee,
    color: 'from-emerald-600 to-green-600',
    type: 'starbucks'
  };

  // A trip first: Miles are worth the most there (1.25¢ vs 1¢ as a gift
  // card), and spending them on Defera keeps the value in the customer's
  // next trip. Then gift cards, then the add-ons.
  const tripOption = {
    miles: '100+',
    benefit: 'Your Next Trip',
    description: `Worth ${MILES_TRIP_CENTS_PER_MILE}¢ each at checkout, ${MILES_TRIP_BONUS_PERCENT}% more than as a gift card`,
    icon: Plane,
    color: 'from-brand-500 to-brand-300',
    type: 'trip'
  };
  const redeemOptions = [tripOption, starbucksOption, ...rewardTiers];

  // Both entry points land in the same overlay; the server re-validates
  // everything against the ledger, so prefills are conveniences only.
  const openRedeemModal = ({ merchant = '', miles = '' } = {}) => {
    setRedeemPrefill({ merchant, miles });
    setRedeemModalOpen(true);
  };

  const handleRedeemNow = () => {
    const milesToRedeem = parseInt(calculatorMiles, 10) || 0;
    if (milesToRedeem <= 0 || !selectedMerchant) return;
    openRedeemModal({ merchant: selectedMerchant, miles: String(milesToRedeem) });
  };

  const handleRedeemClick = (option) => {
    if (option.type === 'trip') {
      navigate('/search');
    } else if (option.type === 'starbucks') {
      openRedeemModal();
    } else if (option.type === 'travel') {
      navigate('/trips');
    }
  };

  // Lifestyle / editorial sections (images in public/images/rewards/)
  const lifestyleSections = [
    {
      image: '/images/rewards/reward1.jpg',
      imageAlt: 'Travel',
      title: 'Travel Further',
      subtitle: `Your Miles go ${MILES_TRIP_BONUS_PERCENT}% further on a Defera trip. Apply them right at checkout. Every trip brings you closer to the next one.`,
      cta: 'Book Travel',
      ctaHref: '/search',
      layout: 'imageRight',
    },
    {
      image: '/images/rewards/reward2.jpg',
      imageAlt: 'Shop & Dine',
      title: 'Shop Where You Love',
      subtitle: 'Redeem Miles for e-gift cards at 200+ brands—from coffee to fashion, dining to entertainment.',
      layout: 'imageLeft',
    },
    {
      image: '/images/rewards/girls.jpg',
      imageAlt: 'Earn',
      title: 'Earn on Every Dollar',
      subtitle: '2 Miles per dollar on every completed trip. No caps, no limits—just rewards that add up.',
      layout: 'imageRight',
    },
    {
      image: '/images/rewards/reward4.jpg',
      imageAlt: 'Rewards',
      title: 'Redeem Your Way',
      subtitle: 'Apply Miles toward your next trip, upgrades, or gift cards. Your rewards, your choice.',
      layout: 'imageLeft',
    },
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Miles Post at Payoff',
      description: 'Miles land in your account as soon as your trip is paid in full'
    },
    {
      icon: Globe,
      title: 'Miles Never Expire',
      description: 'Your Miles remain valid as long as your account is in good standing'
    },
    {
      icon: Shield,
      title: 'No Blackout Dates',
      description: 'Redeem anytime—no restrictions during peak travel or holidays'
    },
    {
      icon: TrendingUp,
      title: 'Generous Earning Rate',
      description: 'Earn 2 Miles per dollar on all eligible travel purchases'
    }
  ];

  return (
    <div className="min-h-screen bg-brand-950 text-white relative overflow-x-hidden">
      <PageSeo path="/rewards" />
      {/* Royal blue ambient wash */}
      <RoyalAmbientBackground />

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              {/* Logo */}
              <motion.img
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                src="/defera-logo-web.png"
                alt="Defera Logo"
                className="h-12 mx-auto mb-6"
              />

              {/* Title */}
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-thin mb-4 tracking-tight">
                Defera Miles
              </h1>
              
              {/* Subtitle */}
              <p className="text-lg sm:text-xl text-white/60 font-light mb-12 max-w-2xl mx-auto">
                The rewards program that rewards every dollar you spend on travel
              </p>

              {/* Earn Rate Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="inline-block"
              >
                <div className="border-b border-brand-800/30 pb-6 px-6 py-4 rounded-2xl bg-brand-900/20">
                  <div className="text-6xl sm:text-7xl font-thin tracking-tight text-white">
                    2 Miles
                  </div>
                  <div className="text-white/50 text-sm font-normal mt-1 tracking-wide">
                    per dollar spent on eligible travel
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Miles balance — same glass treatment as Trips detail cards */}
        {user && !isLoading && !isStatusLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-12 sm:mb-16"
          >
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={Gift}
                iconColor="text-brand-200/90"
                accentRgb={BRAND_ROYAL_RGB}
                title="Your miles"
                subtitle={user.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : user.email?.split('@')[0]}
              />
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-brand-900/40 border border-brand-800/30 shrink-0">
                      <img src="/defera-logo-web.png" alt="" className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white/45 text-[10px] uppercase tracking-[0.12em] font-medium">Balance</p>
                      <p className="text-2xl sm:text-3xl font-semibold text-brand-200/95 tabular-nums">
                        {milesBalance.toLocaleString()}
                      </p>
                      {/* Under the checkout minimum, Miles take nothing off a trip yet. */}
                      {milesBalance >= MILES_CHECKOUT_MIN ? (
                        <p className="mt-1 text-sm font-normal text-white/70">
                          Worth <span className="font-medium text-white tabular-nums">${fmtCurrency(tripCentsForMiles(milesBalance))}</span> off your next trip
                          <span className="text-white/45"> · ${fmtCurrency(giftCardCentsForMiles(milesBalance))} as gift cards</span>
                        </p>
                      ) : milesBalance > 0 ? (
                        <p className="mt-1 text-sm font-normal text-white/70">
                          You can use Miles on a trip once you have {MILES_CHECKOUT_MIN.toLocaleString()}.
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 w-full sm:w-auto">
                    <Button
                      type="button"
                      className="h-9 flex-1 sm:flex-none rounded-full bg-white text-black hover:bg-white/90 text-xs font-medium"
                      onClick={() => navigate('/search')}
                    >
                      Use on a trip
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 flex-1 sm:flex-none rounded-full border-brand-700/40 bg-brand-900/35 text-white/90 hover:bg-brand-800/40 hover:text-white text-xs font-medium"
                      onClick={() => {
                        calculatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      Gift cards
                    </Button>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[11px] text-white/[0.5] font-medium mb-2">
                    <span>{tierProgressCaption}</span>
                    <span>{Math.round(tierProgressPercent)}%</span>
                  </div>
                  <div className="relative h-2 bg-brand-950/60 rounded-full overflow-hidden border border-brand-800/25">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${tierProgressPercent}%` }}
                      transition={{ duration: 1.5, ease: 'easeOut', delay: 0.9 }}
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-brand-300/90 to-brand-200/80"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Live redemption / service requests — same card as Trips, so a
            submitted e-gift card request is visible right where it was made */}
        {user && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-16">
            <ServiceInquiryCard />
          </div>
        )}

        {/* Card-linked Miles (Fidel). Hidden until launch. */}
        {user && CARD_LINKED_MILES_ENABLED && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-16">
            <ShopAndEarnSection />
          </div>
        )}

        {/* Earn Defera Credit by posting about a trip. Renders nothing until
            the program is on (EARN_CREDIT_ENABLED). */}
        {user && <EarnCreditPromo className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-16" />}

        {/* ============================================================ */}
        {/* DEFERA STATUS — Tier program section                        */}
        {/* ============================================================ */}
        <div id="defera-status" className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-12 md:py-14 scroll-mt-24 space-y-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-brand-800/30 pb-8">
            <div className="min-w-0">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-thin text-white tracking-tight leading-none">
                Defera Status
              </h2>
              <p className="text-white/65 text-sm font-normal leading-relaxed max-w-xl mt-4">
                Spend on completed trips in each calendar year to unlock tiers for the next benefit cycle.
              </p>
            </div>
            {user && userStatus && (
              <div className="flex items-stretch gap-8 sm:gap-10 shrink-0">
                <div className="min-w-0">
                  <span className="text-white/30 uppercase tracking-[0.14em] text-[10px] font-normal block mb-1.5">
                    Active today
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-white text-base font-normal capitalize">
                      {userStatus.active?.tier || 'member'}
                    </span>
                    <MyVipBadge />
                  </span>
                </div>
                <div className="min-w-0 border-l border-brand-800/30 pl-8 sm:pl-10">
                  <span className="text-white/30 uppercase tracking-[0.14em] text-[10px] font-normal block mb-1.5">
                    QY spend
                  </span>
                  <span className="text-white text-base font-normal tabular-nums">
                    ${Math.round(userStatus.in_progress?.qd_dollars || 0).toLocaleString()}
                    {typeof userStatus.in_progress?.qualifying_year === 'number' && (
                      <span className="text-white/30 ml-2">· {userStatus.in_progress.qualifying_year}</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={TrendingUp}
                iconColor="text-brand-300"
                accentRgb="56,189,248"
                title="Your progress"
                subtitle="Current tier vs. projection from this year's qualifying dollars"
              />
              <div className="p-5 sm:p-6 space-y-5">
                {user && userStatus ? (
                  <div className={`${BRAND_NESTED_PANEL} p-5 sm:p-6`}>
                    <StatusProgressCard status={userStatus} variant="embedded" />
                  </div>
                ) : (
                  <div className={`${BRAND_NESTED_PANEL} p-5 sm:p-6 text-sm text-white/[0.64] font-normal leading-relaxed`}>
                    Sign in to see qualifying spend and your tier projection. Accelerated earning applies once Defera Status is active on your account.
                  </div>
                )}
                <div className={`${BRAND_NESTED_PANEL} p-5 sm:p-6 text-sm text-white/[0.64] font-normal leading-relaxed`}>
                  Reach Silver, Gold, Platinum, or Diamond based on cumulative qualifying dollars. Higher tiers multiply miles on new completed flights and unlock travel perks such as gift cards, checked bags, and preferred seats.
                </div>
              </div>
            </div>

          <div>
            <div className="mb-6">
              <h3 className="text-xl sm:text-2xl font-light tracking-tight text-white">
                Tier benefits
              </h3>
              <p className="text-white/40 text-xs font-normal mt-1">
                Spend thresholds · earn rate · included perks
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              {TIERS.map((tier, idx) => {
                const benefits = TIER_BENEFITS[tier.id] || [];
                const isUserTier = userStatus?.active?.tier === tier.id;
                const theme = TIER_THEME[tier.id] || TIER_THEME.member;
                const Icon = TIER_ICON[tier.id] || Star;
                const mult = tier.multiplier ?? 1;
                return (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '0px 0px -80px 0px' }}
                    transition={{ duration: 0.38, delay: idx * 0.05 }}
                    className={cn(
                      'relative flex h-full flex-col overflow-hidden rounded-2xl border backdrop-blur-sm min-w-0',
                      theme.border,
                      isUserTier ? cn('bg-brand-900/50', theme.glow) : 'bg-brand-950/40',
                    )}
                  >
                    {/* Tier accent bar — the tier's identity color */}
                    <div className={cn('h-1 w-full bg-gradient-to-r', theme.gradient)} />

                    <div className="flex flex-1 flex-col p-5">
                      {/* Icon chip + tier label */}
                      <div className="mb-4 flex items-center justify-between gap-2">
                        <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', theme.chip)}>
                          <Icon className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        {isUserTier ? (
                          <span className={cn('rounded-full px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.12em]', theme.chip)}>
                            Your tier
                          </span>
                        ) : (
                          <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-white/25">
                            {tier.id === 'member' ? 'Starting' : 'Status tier'}
                          </span>
                        )}
                      </div>

                      {/* Tier name — themed */}
                      <h4 className={cn('mb-3 text-lg font-medium tracking-tight', theme.text)}>
                        {tier.name}
                      </h4>

                      {/* Spend threshold */}
                      <div className="mb-4 flex flex-col">
                        <span className="text-[27px] font-thin leading-none tracking-tight tabular-nums text-white">
                          ${tier.floor.toLocaleString()}
                        </span>
                        <span className="mt-1.5 text-[9.5px] font-semibold uppercase tracking-widest text-white/30">
                          {tier.floor > 0 ? 'Annual spend' : 'No spend required'}
                        </span>
                      </div>

                      {/* Earn rate — boxed hero stat with the multiplier chip */}
                      <div className="mb-5 flex items-center justify-between gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
                        <span className="text-sm font-normal text-white/90">{tier.earnRate}</span>
                        <span className={cn('shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium', theme.chip)}>
                          {mult > 1 ? `${mult}×` : 'Base'}
                        </span>
                      </div>

                      {/* Benefits */}
                      <p className="mb-2.5 text-[9.5px] font-semibold uppercase tracking-widest text-white/30">
                        Benefits
                      </p>
                      <ul className="flex flex-1 flex-col gap-2">
                        {benefits.map((b) => (
                          <li
                            key={b.key}
                            className="flex items-start gap-2 text-[11.5px] font-normal leading-relaxed text-white/60"
                          >
                            <Check className={cn('mt-0.5 h-3 w-3 flex-shrink-0', theme.accent)} strokeWidth={2.5} />
                            <span className="min-w-0">{b.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowTerms((prev) => !prev)}
              aria-expanded={showTerms}
              aria-controls="defera-status-terms"
              className="inline-flex items-center gap-2 rounded-full border border-brand-700/40 bg-brand-900/30 text-white px-6 py-2.5 text-sm font-normal hover:bg-brand-800/40 transition-colors duration-200"
            >
              {showTerms ? 'Hide program & terms' : 'View program & terms'}
            </button>
          </div>

          {showTerms && (
            <div
              id="defera-status-terms"
              className={cn(
                REWARDS_SECTION_CARD,
                'ring-1 ring-emerald-400/20 shadow-[0_0_40px_-12px_rgba(16,185,129,0.15),inset_0_1px_0_rgba(255,255,255,0.06)]',
              )}
            >
              <SectionBand
                icon={Shield}
                iconColor="text-brand-300"
                accentRgb="52,211,153"
                title="Program guide"
                subtitle="How it works · founding cohort · legal terms"
                titleClassName="text-white font-medium text-xs uppercase tracking-widest truncate"
              />
              <div className="border-t border-white/[0.05] bg-gradient-to-b from-emerald-950/[0.12] via-transparent to-brand-950/[0.08]">
                <div className="p-4 sm:p-5 md:p-6 space-y-5 md:space-y-6">
                  {/* How it works — three matched glass cards (distinct header hues) */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:gap-5">
                    {[
                      {
                        step: '01',
                        title: 'Spend & fly',
                        body: 'Every qualifying dollar from a completed Defera booking counts toward your tier. Departed segments only—not holds or installments still in flight.',
                        header: 'from-emerald-500/30 via-cyan-500/15 to-brand-600/22',
                        stepNum: 'text-emerald-100/95',
                      },
                      {
                        step: '02',
                        title: 'Earn status',
                        body: 'Crossing a published annual spend threshold activates your tier for the next benefit calendar year, with a short grace period each January to requalify.',
                        header: 'from-brand-500/28 via-brand-500/16 to-brand-600/24',
                        stepNum: 'text-brand-100/95',
                      },
                      {
                        step: '03',
                        title: 'Enjoy perks',
                        body: 'Earn accelerated miles on future completed travel and use published tier benefits—such as bags, seats, and partner offers—subject to availability and annual limits described in your tier.',
                        header: 'from-brand-500/28 via-brand-400/14 to-brand-500/18',
                        stepNum: 'text-brand-100/95',
                      },
                    ].map((item, idx) => (
                    <motion.article
                      key={item.step}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '0px 0px -48px 0px' }}
                      transition={{ duration: 0.36, delay: idx * 0.07 }}
                      className={cn(
                        PROGRAM_GUIDE_SHELL,
                        'flex min-h-0 min-w-0 flex-col ring-1 ring-white/[0.07]',
                      )}
                    >
                      <div
                        className={cn(
                          'relative shrink-0 border-b border-white/[0.10] bg-gradient-to-br px-4 py-3 sm:px-5 sm:py-3.5',
                          item.header,
                        )}
                      >
                        <div
                          className="pointer-events-none absolute inset-0 opacity-[0.38]"
                          style={{
                            background:
                              'radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.16) 0%, transparent 55%)',
                          }}
                          aria-hidden
                        />
                        <p className="relative text-[10px] uppercase tracking-[0.14em] text-white/65 font-medium">Step</p>
                        <p className={cn('relative mt-1 text-2xl font-semibold tabular-nums tracking-tight', item.stepNum)}>{item.step}</p>
                      </div>
                      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 sm:p-5">
                        <h4 className="text-[13px] sm:text-sm font-medium text-white tracking-tight leading-snug mb-2">
                          {item.title}
                        </h4>
                        <p className="text-[11px] sm:text-xs text-white/[0.72] font-normal leading-relaxed">{item.body}</p>
                      </div>
                    </motion.article>
                  ))}
                </div>

                {/* Founding cohort — accent glass card */}
                <motion.article
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '0px 0px -48px 0px' }}
                  transition={{ duration: 0.36, delay: 0.08 }}
                  className={cn(
                    PROGRAM_GUIDE_SHELL,
                    'border-brand-400/22 ring-1 ring-brand-300/25 shadow-[0_0_48px_-16px_rgba(10, 18, 51,0.18)]',
                  )}
                >
                  <div
                    className={cn(
                      'relative shrink-0 border-b border-white/[0.08] bg-gradient-to-br px-4 py-3.5 sm:px-5 sm:py-4',
                      'from-brand-400/30 via-brand-500/14 to-brand-600/16',
                    )}
                  >
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.38]"
                      style={{
                        background:
                          'radial-gradient(115% 75% at 0% 0%, rgba(255,255,255,0.14) 0%, transparent 52%)',
                      }}
                      aria-hidden
                    />
                    <div className="relative flex items-center gap-3 sm:gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-brand-300/30 bg-brand-400/18 backdrop-blur-sm">
                        <Sparkles className="h-5 w-5 text-brand-100/95" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 py-0.5">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-brand-100/65 font-medium">Launch window</p>
                        <h4 className="text-sm sm:text-[0.9375rem] font-medium text-white tracking-tight leading-snug">
                          Founding member period
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 md:px-6 md:pb-6 md:pt-5">
                    <p className="text-[11px] sm:text-xs text-white/[0.72] font-normal leading-relaxed max-w-3xl">
                      Bookings with departures January 1, 2024 through December 31, 2025 count toward inaugural 2026 tiering. Benefit
                      window extends through January 31, 2027 for cohort members.
                    </p>
                  </div>
                </motion.article>

                {/* Terms — formal program agreement */}
                <motion.article
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '0px 0px -48px 0px' }}
                  transition={{ duration: 0.36, delay: 0.06 }}
                  className={cn(
                    PROGRAM_GUIDE_SHELL,
                    'flex flex-col min-w-0 ring-1 ring-brand-300/22 border-brand-200/14 shadow-[0_0_52px_-18px_rgba(16, 37, 178,0.2)]',
                  )}
                >
                  <div className="relative shrink-0 border-b border-brand-200/15 bg-gradient-to-br from-brand-600/35 via-brand-500/22 to-emerald-900/28 px-4 py-4 sm:px-5 sm:py-[1.125rem]">
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.4]"
                      style={{
                        background:
                          'radial-gradient(115% 90% at 20% 0%, rgba(255,255,255,0.2) 0%, transparent 55%), radial-gradient(90% 60% at 100% 0%, rgba(16,185,129,0.18) 0%, transparent 45%)',
                      }}
                      aria-hidden
                    />
                    <div className="relative flex items-start gap-3.5 sm:gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-brand-700/35 bg-brand-900/45 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                        <FileText className="h-5 w-5 text-brand-100/95" strokeWidth={1.65} />
                      </div>
                      <div className="min-w-0 pt-0.5 space-y-1">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-white/65 font-medium leading-snug">
                          Defera Status Program
                        </p>
                        <h3 className="text-[0.9375rem] sm:text-base font-semibold text-white tracking-tight leading-snug">
                          Terms and Conditions of Participation
                        </h3>
                        <p className="text-[10px] sm:text-[11px] text-white/[0.55] font-normal leading-relaxed pt-1 max-w-xl">
                          These terms supplement the general Defera Terms of Use and Rewards materials. Defined terms carry the meanings set out below unless otherwise clarified.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 border-t border-white/[0.04] bg-gradient-to-b from-brand-950/[0.2] via-slate-950/30 to-transparent p-5 sm:p-6 md:p-8">
                    <div className={cn(BRAND_NESTED_ROW, 'px-4 py-4 sm:px-5 sm:py-4.5 mb-7 md:mb-8')}>
                      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-200/75 font-medium mb-2">Acceptance</p>
                      <p className="text-[11px] sm:text-xs text-white/[0.72] font-normal leading-[1.65]">
                        Participation in Defera Status confirms that you agree to abide by these Terms in addition to the general terms governing bookings and Rewards. Benefits, thresholds, and published limits may vary by tier as shown in the Rewards experience.
                      </p>
                    </div>

                    <div className="space-y-7 md:space-y-9 text-[11px] sm:text-xs leading-[1.7]">
                      <section className="min-w-0" aria-labelledby="pg-s1">
                        <h4 id="pg-s1" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 1 — Qualifying spend and completed travel
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">1.1&nbsp;</span>
                            &ldquo;Qualifying Spend&rdquo; is the cumulative eligible dollars associated with itineraries that satisfy the Completed Booking standard in Clause 1.3, counted after travel has commenced under that itinerary and reflecting the total paid-in amount Defera records for qualifying payment methods—without double counting amounts still pending or attributable solely to itineraries that ultimately fail to qualify.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">1.2&nbsp;</span>
                            Bookings paid entirely with Defera Miles generally do&nbsp;<strong className="text-white/[0.88]">not</strong>&nbsp;contribute Qualifying Spend and ordinarily do&nbsp;<strong className="text-white/[0.88]">not</strong>&nbsp;earn incremental miles attributable solely to redemption.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">1.3&nbsp;</span>
                            A booking is&nbsp;<strong className="text-white/[0.88]">Completed</strong>&nbsp;for Status purposes once (i) outbound travel attributable to such booking has begun, (ii) all scheduled segments have departed or been otherwise honoured per carrier rules accepted at purchase, and (iii) outstanding balances invoiced through Defera for that itinerary are paid per the payment plan or policy in force at departure. Reservations cancelled prior to commencement, itineraries still pending travel, unpaid balances beyond applicable grace allowances, or similar exceptions do&nbsp;<strong className="text-white/[0.88]">not</strong>&nbsp;count unless Defera expressly states otherwise in writing.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s2">
                        <h4 id="pg-s2" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 2 — Status cycles, Benefit Periods, and requalification
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">2.1&nbsp;</span>
                            Status is tied to accumulation of Qualifying Spend during each successive&nbsp;<strong className="text-white/[0.88]">Qualifying Calendar Year</strong>&nbsp;Defera designates in the Rewards interface.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">2.2&nbsp;</span>
                            Status ordinarily becomes effective prospectively beginning the calendar year&nbsp;<strong className="text-white/[0.88]">immediately following</strong>&nbsp;a completed Qualifying Calendar Year in which the applicable threshold was met. Each granted Status includes a Benefit Period aligning with calendar months Defera specifies; subject to Clause 2.3,&nbsp;a&nbsp;<strong className="text-white/[0.88]">grace window through January&nbsp;31</strong>&nbsp;succeeding the scheduled conclusion of such Benefit Period may apply to allow reaffirmation of eligibility.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">2.3&nbsp;</span>
                            If narrative illustrations elsewhere conflict with dashboards, calculators, tier tables, or other official Program copy within the Application, those in-product materials govern for dates, tiers, thresholds, and effective periods.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s3">
                        <h4 id="pg-s3" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 3 — Mile multiples and retrospective changes
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">3.1&nbsp;</span>
                            Higher-tier mileage multipliers,&nbsp;if shown for your tier, apply&nbsp;<strong className="text-white/[0.88]">only</strong>&nbsp;to bookings that complete&nbsp;<strong className="text-white/[0.88]">after</strong>&nbsp;the Status furnishing such multipliers becomes effective. Prior journeys are not reopened or recomputed purely because you attained a subsequent tier thereafter.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">3.2&nbsp;</span>
                            If your Status declines in accordance with these Terms, mileage already deposited for qualifying activity ordinarily remains unaffected except where Defera exercises remedies under Clause 9.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s4">
                        <h4 id="pg-s4" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 4 — Birthday bonuses
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">4.1&nbsp;</span>
                            Tier-based birthday mileage bonuses, where offered,&nbsp;crediting no more frequently than&nbsp;<strong className="text-white/[0.88]">once per Qualifying Calendar Year</strong>, are determined using&nbsp;<strong className="text-white/[0.88]">Pacific Time (Los&nbsp;Angeles)</strong>.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">4.2&nbsp;</span>
                            Eligibility derives from traveler date-of-birth information present in&nbsp;<strong className="text-white/[0.88]">Defera-maintained traveler profiles associated with your account</strong>; where multiple profiles conflict, Defera will reference the traveler record most recently amended that includes an explicit birth date. Failure to retain compliant profile information before the cutoff Defera prescribes excuses any birthday bonus issuance for such Qualifying Calendar Year without liability.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s5">
                        <h4 id="pg-s5" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 5 — Ancillary Benefits; caps; orderly application
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">5.1&nbsp;</span>
                            Ancillary perks associated with tiers—such as checked baggage allowances, preferred seating where published, concierge assistance, merchant gift cards offered by Defera, or analogous benefits—remain&nbsp;<strong className="text-white/[0.88]">subject to inventory, contractual limits with suppliers, geography, aviation regulations, fare rules,</strong>&nbsp;and other operational constraints notwithstanding marketing summaries.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">5.2&nbsp;</span>
                            Each ancillary benefit&nbsp;<strong className="text-white/[0.88]">must be formally requested through Defera&apos;s authenticated channels&nbsp;</strong>and approved while your Status entitlement is active unless Defera provides a permitted alternative in writing.&nbsp;Ancillary issuance is allocated first-come-first-served&nbsp;<strong className="text-white/[0.88]">among eligible bookings occurring within the Benefit Period</strong>; once published annual counts or capped dollar equivalents are exhausted, additional discretionary fulfillment is withheld until the succeeding allocation window.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">5.3&nbsp;</span>
                            Personnel&nbsp;<strong className="text-white/[0.88]">may not&nbsp;</strong>override published benefit caps&nbsp;<strong className="text-white/[0.88]">without Defera supervisory authorization.&nbsp;</strong>Inconsistencies should use documented customer-resolution channels rather than informal workarounds.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">5.4&nbsp;</span>
                            Where itineraries qualify for overlapping benefits,&nbsp;<strong className="text-white/[0.88]">application follows confirmation timestamps</strong>&nbsp;(earlier-confirmed itineraries consume available capacity before later bookings at the identical tier classification).
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s6">
                        <h4 id="pg-s6" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 6 — Preferred seating and member cost-share
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">6.1&nbsp;</span>
                            Complimentary or rebated&nbsp;<strong className="text-white/[0.88]">preferred seat&nbsp;</strong>benefits expressly reference advertised dollar ceilings per itinerary segment; when prevailing seat selections exceed permitted ceilings&nbsp;<strong className="text-white/[0.88]">you remain responsible&nbsp;</strong>for incremental charges Defera or the carrier invoices.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">6.2&nbsp;</span>
                            Taxes, governmental fees, or carrier-imposed surcharges remain your responsibility notwithstanding benefit credits except where statutes mandate otherwise or Defera elects gratuitous subsidy.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s7">
                        <h4 id="pg-s7" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 7 — Priority support obligations
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">7.1&nbsp;</span>
                            Concierge and priority-support coverage hours may change with notice communicated through customary Defera channels; Platinum-or-higher illustrative availability references&nbsp;<strong className="text-white/[0.88]">goals—not guarantees&nbsp;</strong>of instantaneous live assistance at arbitrary hours absent regulatory duty.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">7.2&nbsp;</span>
                            Force majeure, labour measures, unusually high enquiry volumes,&nbsp;<strong className="text-white/[0.88]">major carrier disruptions,</strong>&nbsp;or similar circumstances may lengthen response times;&nbsp;<strong className="text-white/[0.88]">such delays alone&nbsp;</strong><strong className="text-white/[0.88]">do not&nbsp;</strong>constitute a breach of these Terms.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s8">
                        <h4 id="pg-s8" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 8 — Program revisions
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">8.1&nbsp;</span>
                            Defera may modify or discontinue program features—including tier thresholds, benefits, calculators, timelines, geography, founding cohort distinctions, mileage factors,&nbsp;<strong className="text-white/[0.88]">or related materials</strong>—upon&nbsp;<strong className="text-white/[0.88]">commercially reasonable notice</strong> through in-product disclosures, electronic communications, or other customary channels.&nbsp;A Status&nbsp;<strong className="text-white/[0.88]">already granted ordinarily remains effective</strong>&nbsp;through its stated Benefit Period unless forfeiture arises under Section&nbsp;9 or applicable law directs otherwise.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">8.2&nbsp;</span>
                            Benefits relying on airlines, lodging partners,&nbsp;<strong className="text-white/[0.88]">merchant offers,</strong>&nbsp;or analogous programs&nbsp;<strong className="text-white/[0.88]">remain subject&nbsp;</strong>to those counterparties maintaining capacity, complying with applicable law, renewing agreements with Defera,&nbsp;<strong className="text-white/[0.88]">and enforcing prevailing fare&nbsp;</strong>or ancillary rules at booking.&nbsp;<strong className="text-white/[0.88]">Illustrative marketing&nbsp;</strong>does&nbsp;<strong className="text-white/[0.88]">not&nbsp;</strong>guarantee identical future goodwill if partner economics change materially.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s9">
                        <h4 id="pg-s9" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 9 — Fraudulent conduct; chargebacks; suspension; forfeiture
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">9.1&nbsp;</span>
                            Defera may withhold, claw back, delay, or void mileage; downgrade or revoke Status; refuse ancillary benefits; suspend or close accounts; cooperate with card brands, carriers, and regulators; or pursue other lawful remedies where a reasonable factual basis suggests&nbsp;<strong className="text-white/[0.88]">fraud, abuse, repeated improper chargebacks, identity misrepresentation,</strong>&nbsp;<strong className="text-white/[0.88]">or deliberate violation</strong>&nbsp;of these Terms or overarching Defera policies materially affecting integrity of Status or ancillary fulfillment.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">9.2&nbsp;</span>
                            Disputed charges addressed in&nbsp;<strong className="text-white/[0.88]">good faith</strong>&nbsp;through escalation procedures Defera publishes ordinarily do&nbsp;<strong className="text-white/[0.88]">not</strong>, standing alone, trigger Section&nbsp;9 remedies absent corroborative abuse indicators.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">9.3&nbsp;</span>
                            Investigations and enforcement actions Defera documents in ordinary course after reasonable diligence control unless applicable law mandates a differing administrative remedy you pursue successfully.
                          </p>
                        </div>
                      </section>

                      <section className="min-w-0" aria-labelledby="pg-s10">
                        <h4 id="pg-s10" className="text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.08em] text-brand-100/95 mb-3 pb-2 border-b border-white/[0.1]">
                          Section 10 — Non-transferability; companions; valuations
                        </h4>
                        <div className="space-y-2.5 text-white/[0.7] font-normal border-l-2 border-white/[0.08] pl-3 sm:border-l-[3px] sm:border-emerald-400/25 sm:pl-3.5">
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">10.1&nbsp;</span>
                            Defera Status tiers, Status benefits conferred through the Program, and accrued Program miles credited in good faith&nbsp;<strong className="text-white/[0.88]">have no standalone cash redemption value,</strong>&nbsp;except where applicable law requires otherwise.&nbsp;<strong className="text-white/[0.88]">They are non-transferable&nbsp;</strong>and&nbsp;<strong className="text-white/[0.88]">may not&nbsp;</strong>be sold, commercially assigned, or offered as unrelated collateral.&nbsp;<strong className="text-white/[0.88]">Illustrative marketing copy does not, by itself, create assignable rights&nbsp;</strong>beyond applicable written Program disclosures.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">10.2&nbsp;</span>
                            Companion allowances reference&nbsp;<strong className="text-white/[0.88]">guests travelling on itineraries tied to validated qualifying-member reservations</strong> unless Defera authorizes substitutions consistent with partner name-change rules published at booking.
                          </p>
                          <p>
                            <span className="font-semibold text-white/[0.88] tabular-nums">10.3&nbsp;</span>
                            If a competent authority holds any provision here unenforceable, the balance of these Terms remains in effect to the fullest extent permitted by law.
                          </p>
                        </div>
                      </section>
                    </div>

                    <p className="mt-8 border-t border-white/[0.08] pt-5 text-[10px] sm:text-[11px] text-white/[0.48] font-normal text-center md:text-right leading-relaxed">
                      Published electronically for participant reference. Informal summaries or collateral do not supersede&nbsp;<strong className="font-semibold text-white/[0.68]">this Exhibit</strong>&nbsp;except where statute prescribes clearer mandatory disclosures for consumers in your jurisdiction.
                    </p>
                  </div>
                </motion.article>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lifestyle / Editorial Sections - mobile: photo then text per section; desktop: 2-col alternating grid */}
        {/* Mobile: stacked sections, each with image then text */}
        <div className="lg:hidden flex flex-col">
          {lifestyleSections.map((section) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              className="flex flex-col"
            >
              <div className="min-h-[55vh] relative overflow-hidden bg-gray-900 aspect-[3/4]">
                <img
                  src={section.image}
                  alt={section.imageAlt}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement?.querySelector('[data-placeholder]');
                    if (placeholder) placeholder.classList.remove('hidden');
                  }}
                />
                <div data-placeholder className="absolute inset-0 flex items-center justify-center hidden">
                  <span className="text-white/30 text-xs font-normal text-center px-4">Add image to public{section.image}</span>
                </div>
              </div>
              <div className="flex items-center justify-center p-8 sm:p-12 min-h-[40vh] bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900">
                <div className="max-w-md">
                  <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">
                    {section.title}
                  </h2>
                  <p className="text-white/60 font-light leading-relaxed text-base sm:text-lg">
                    {section.subtitle}
                  </p>
                  {section.cta && (
                    <a
                      href={section.ctaHref}
                      className="inline-block mt-6 border border-white/40 text-white px-6 py-2.5 text-sm font-normal hover:bg-white hover:text-black transition-colors duration-200"
                    >
                      {section.cta}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Desktop: 2-col grid, images touch at corners */}
        <div className="hidden lg:grid grid-cols-2 gap-0 max-w-[3000px] mx-auto">
          {lifestyleSections.map((section) => (
            <React.Fragment key={section.title}>
              {section.layout === 'imageRight' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                    className="flex items-center justify-center p-8 sm:p-12 lg:p-16 min-h-[70vh] bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900"
                  >
                    <div className="max-w-md">
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-4">
                        {section.title}
                      </h2>
                      <p className="text-white/60 font-light leading-relaxed text-base sm:text-lg">
                        {section.subtitle}
                      </p>
                      {section.cta && (
                        <a
                          href={section.ctaHref}
                          className="inline-block mt-6 border border-white/40 text-white px-6 py-2.5 text-sm font-normal hover:bg-white hover:text-black transition-colors duration-200"
                        >
                          {section.cta}
                        </a>
                      )}
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                    className="min-h-[70vh] max-h-[1500px] max-w-[1500px] relative overflow-hidden bg-gray-900"
                  >
                    <img
                      src={section.image}
                      alt={section.imageAlt}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.parentElement?.querySelector('[data-placeholder]');
                        if (placeholder) placeholder.classList.remove('hidden');
                      }}
                    />
                    <div data-placeholder className="absolute inset-0 flex items-center justify-center hidden">
                      <span className="text-white/30 text-xs font-normal text-center px-4">Add image to public{section.image}</span>
                    </div>
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                    className="min-h-[70vh] max-h-[1500px] max-w-[1500px] relative overflow-hidden bg-gray-900"
                  >
                    <img
                      src={section.image}
                      alt={section.imageAlt}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.parentElement?.querySelector('[data-placeholder]');
                        if (placeholder) placeholder.classList.remove('hidden');
                      }}
                    />
                    <div data-placeholder className="absolute inset-0 flex items-center justify-center hidden">
                      <span className="text-white/30 text-xs font-normal text-center px-4">Add image to public{section.image}</span>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                    className="flex items-center justify-center p-8 sm:p-12 lg:p-16 min-h-[70vh] bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900"
                  >
                    <div className="max-w-md">
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-4">
                        {section.title}
                      </h2>
                      <p className="text-white/60 font-light leading-relaxed text-base sm:text-lg">
                        {section.subtitle}
                      </p>
                      {section.cta && (
                        <a
                          href={section.ctaHref}
                          className="inline-block mt-6 border border-white/40 text-white px-6 py-2.5 text-sm font-normal hover:bg-white hover:text-black transition-colors duration-200"
                        >
                          {section.cta}
                        </a>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Ways to Redeem */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-light tracking-tight mb-12 text-center text-white"
          >
            Redemption Options
          </motion.h2>

          <div className={cn(REWARDS_SECTION_CARD, 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-800/25')}>
            {redeemOptions.map((option, index) => (
              <motion.button
                key={`${option.benefit}-${option.miles}`}
                type="button"
                onClick={() => handleRedeemClick(option)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group relative bg-brand-900/20 p-6 hover:bg-brand-800/30 transition-colors duration-200 text-left cursor-pointer"
              >
                <option.icon className="w-5 h-5 text-white/40 mb-4" strokeWidth={1.5} />
                <div className="text-xl font-light tabular-nums text-white">{option.miles}</div>
                <div className="text-white/30 text-[11px] font-normal uppercase tracking-widest mb-2">Miles</div>
                <div className="text-sm text-white/90 font-normal">{option.benefit}</div>
                <div className="text-xs text-white/60 font-normal mt-0.5">{option.description}</div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* 200+ Merchants Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            className="relative overflow-hidden rounded-lg bg-white p-8 sm:p-12"
          >
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-medium text-gray-900 tracking-tight">
                Redeem at 200+ Participating Merchants
              </h2>
              <p className="text-gray-600 text-sm font-normal mt-1">
                Exchange Miles for e-gift cards at top brands nationwide
              </p>
            </div>
            <p className="text-gray-800 font-normal leading-relaxed mb-8 max-w-2xl">
              Beyond travel, your Miles go further. Redeem for e-gift cards at over 200 participating merchants—including Starbucks, Amazon, Target, Walmart, and hundreds more. Use your rewards where you shop, dine, and live.
            </p>

            <div className="flex flex-col lg:flex-row gap-6 -mx-4 px-2 sm:mx-0 sm:px-0">
              {/* Merchant images - stacked tall scrollable */}
              <div className="flex flex-col items-start gap-1 max-h-[50vh] overflow-y-auto overflow-x-hidden pr-2 flex-shrink-0 -mx-4 sm:mx-0">
                {[10, 1, 2, 3, 11, 5, 4, 7, 12, 8, 6, 9].map((i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-full max-w-[400px] max-h-[1500px] rounded-lg overflow-hidden"
                  >
                    <img
                      src={`/merchants/merchant-${i}.png`}
                      alt={`Merchant options ${i}`}
                      className="w-full h-auto object-contain"
                      style={{ clipPath: 'inset(1% 12% 1% 3%)' }}
                    />
                  </div>
                ))}
              </div>

              {/* Miles to gift card value calculator */}
              <div ref={calculatorRef} id="miles-calculator" className="flex-1 min-w-0 scroll-mt-24">
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 sm:p-6">
                  <h3 className="text-sm font-medium text-gray-900 uppercase tracking-widest mb-4">Miles Value Calculator</h3>
                  <p className="text-gray-600 text-sm font-normal mb-4">
                    See how much your Miles are worth as an e-gift card, and on your next trip
                  </p>
                  <p className="text-gray-600 text-xs font-normal mb-4">
                    Gift card amounts go in $5 increments (500 miles)
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="calculator-merchant" className="block text-sm font-normal text-gray-600 mb-1.5">
                        Merchant <span className="text-gray-400">*</span>
                      </label>
                      <select
                        id="calculator-merchant"
                        value={selectedMerchant}
                        onChange={(e) => setSelectedMerchant(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 font-normal text-base focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500"
                      >
                        <option value="">Select a merchant</option>
                        {calculatorMerchants.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="calculator-miles" className="block text-sm font-normal text-gray-600 mb-1.5">
                        Miles <span className="text-gray-400">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {[500, 1000, 1500, 2000, 2500, 5000, 7500, 10000].map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setCalculatorMiles(String(m))}
                            className="px-3 py-1.5 rounded border border-gray-200 bg-white text-gray-700 text-xs font-normal hover:bg-gray-50"
                          >
                            ${m / 100}
                          </button>
                        ))}
                      </div>
                      <input
                        id="calculator-miles"
                        type="number"
                        min="0"
                        step="100"
                        value={calculatorMiles}
                        onChange={(e) => setCalculatorMiles(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder={user && !isLoading ? String(milesBalance) : 'Enter miles'}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 font-light text-lg focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500"
                      />
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div className="text-sm font-normal text-gray-500 mb-1">E-gift card value</div>
                      <div className="text-2xl font-light text-gray-900">
                        ${fmtCurrency(giftCardCentsForMiles(parseInt(calculatorMiles, 10) || 0))}
                      </div>
                      <p className="mt-2 text-sm font-normal text-gray-600">
                        On your next Defera trip:{' '}
                        <span className="font-medium text-gray-900 tabular-nums">${fmtCurrency(tripCentsForMiles(parseInt(calculatorMiles, 10) || 0))}</span>
                        <span className="text-gray-500"> ({MILES_TRIP_BONUS_PERCENT}% more, taken off the price at checkout)</span>
                      </p>
                    </div>
                    {user && !isLoading && milesBalance > 0 && (
                      <button
                        type="button"
                        onClick={() => setCalculatorMiles(String(milesBalance))}
                        className="text-sm font-normal text-brand-600 hover:text-brand-700"
                      >
                        Use my balance ({milesBalance.toLocaleString()} miles)
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleRedeemNow}
                      disabled={
                        !selectedMerchant ||
                        (parseInt(calculatorMiles, 10) || 0) <= 0 ||
                        !user ||
                        (user && !isLoading && (parseInt(calculatorMiles, 10) || 0) > milesBalance) ||
                        !isValidGiftCardMiles(parseInt(calculatorMiles, 10) || 0)
                      }
                      className={cn('mt-4 w-full py-3 rounded-xl text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors', BRAND_PRIMARY_BTN_CLASS)}
                    >
                      Redeem for E-Gift Card
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Section (formerly Why Defera Miles) */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-light tracking-tight mb-12 text-center text-white"
          >
            Program Benefits
          </motion.h2>

          <div className={cn(REWARDS_SECTION_CARD, 'p-8 sm:p-12')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cn(BRAND_NESTED_ROW, 'p-5 border-l-2 border-l-brand-600/40')}
              >
                <benefit.icon className="w-4 h-4 text-brand-400/80 mb-3" strokeWidth={1.5} />
                <h3 className="text-base font-medium text-white tracking-tight mb-1">
                  {benefit.title}
                </h3>
                <p className="text-sm text-white/65 font-normal leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
            </div>
          </div>
        </div>

        {/* Triple Dip Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            className={cn(REWARDS_SECTION_CARD, 'p-8 sm:p-12')}
          >
            <div className="relative">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white">
                  Triple Your Rewards
                </h2>
                <p className="text-sm text-white/50 font-normal max-w-xl mx-auto mt-2">
                  Maximize value by earning rewards three ways on every qualifying booking
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "0px 0px -50px 0px" }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl font-light text-white/30 mb-4">01</div>
                  <CreditCard className="w-5 h-5 text-white/30 mx-auto mb-3" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white tracking-tight mb-2">
                    Credit Card Rewards
                  </h3>
                  <p className="text-xs text-white/65 font-normal leading-relaxed">
                    Earn your card&apos;s rewards on every travel purchase—same as any other transaction
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "0px 0px -50px 0px" }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-center"
                >
                  <div className="text-4xl font-light text-white/30 mb-4">02</div>
                  <Plane className="w-5 h-5 text-white/30 mx-auto mb-3" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white tracking-tight mb-2">
                    Airline Frequent Flyer Miles
                  </h3>
                  <p className="text-xs text-white/65 font-normal leading-relaxed">
                    Earn airline loyalty miles when you add your frequent flyer number at booking
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "0px 0px -50px 0px" }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-center"
                >
                  <div className="text-4xl font-light text-white/30 mb-4">03</div>
                  <Award className="w-5 h-5 text-white/30 mx-auto mb-3" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white tracking-tight mb-2">
                    Defera Miles
                  </h3>
                  <p className="text-xs text-white/65 font-normal leading-relaxed">
                    Earn 2 Defera Miles per dollar—automatically credited to your account
                  </p>
                </motion.div>
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-10 text-center text-xs text-white/40 font-normal tracking-wide"
              >
                Three rewards programs. One booking.
              </motion.p>
            </div>
          </motion.div>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={cn(REWARDS_SECTION_CARD, 'p-8 sm:p-12')}
          >
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-8 text-center text-white">How the Program Works</h2>
            
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-6 text-white/30 text-sm font-normal">01</div>
                <div>
                  <h3 className="text-sm font-medium text-white tracking-tight mb-1">Book Eligible Travel</h3>
                  <p className="text-sm text-white/65 font-normal leading-relaxed">
                    Earn 2 Miles per dollar on flights, hotels, and packages. Miles are credited automatically once your trip is completed.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-6 text-white/30 text-sm font-normal">02</div>
                <div>
                  <h3 className="text-sm font-medium text-white tracking-tight mb-1">Accumulate Miles</h3>
                  <p className="text-sm text-white/65 font-normal leading-relaxed">
                    Monitor your balance in your account. Miles never expire and accumulate with every qualifying purchase.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-6 text-white/30 text-sm font-normal">03</div>
                <div>
                  <h3 className="text-sm font-medium text-white tracking-tight mb-1">Redeem for Travel & More</h3>
                  <p className="text-sm text-white/65 font-normal leading-relaxed">
                    Apply Miles toward seating, baggage, cabin upgrades, or travel. No blackout dates.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-light tracking-tight mb-12 text-center text-white"
          >
            Program FAQs
          </motion.h2>

          <div className={cn(REWARDS_SECTION_CARD, 'p-8 sm:p-12')}>
            <div className="space-y-4">
            {[
              {
                q: 'How do I earn Defera Miles?',
                a: 'Earn 2 Defera Miles for every $1 spent on flights, hotels, and travel packages booked through Defera.'
              },
              {
                q: 'Do my Miles expire?',
                a: 'No. Your Defera Miles remain valid as long as your account is in good standing.'
              },
              {
                q: 'Can I combine Miles with other offers?',
                a: 'Yes. Defera Miles may be combined with promotional offers and discounts for maximum value.'
              },
              {
                q: 'How do I redeem my Miles?',
                a: `At checkout, your Miles balance sits right above your card. Tap Apply to trip to use as many as the trip allows, or choose an amount. You need at least ${MILES_CHECKOUT_MIN.toLocaleString()} Miles to use them. On a trip each Mile is worth ${MILES_TRIP_CENTS_PER_MILE}¢, ${MILES_TRIP_BONUS_PERCENT}% more than as a gift card. You can also redeem them here for e-gift cards at 200+ brands, at 1¢ per Mile.`
              },
              {
                q: 'Can I use Miles and installments together?',
                a: 'Yes. Your Miles lower the trip price first, and your payment plan covers the rest. If your Miles cover the whole trip, you can book it with Miles alone, no card needed. That isn’t offered on Flight + Hotel packages, promo trips, or trips with an airline travel credit applied. Miles can’t be combined with travel insurance.'
              },
              {
                q: 'What happens to my Miles if I cancel?',
                a: 'If a trip is cancelled, the Miles you used on it come back to your account.'
              },
              {
                q: 'Are there blackout dates?',
                a: 'No. Redeem anytime—including holidays and peak travel periods.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="border-b border-brand-800/25 py-5 first:pt-0 last:border-b-0"
              >
                <h3 className="text-sm font-medium text-white tracking-tight mb-2">
                  {faq.q}
                </h3>
                <p className="text-sm text-white/85 font-normal leading-relaxed">
                  {faq.a}
                </p>
              </motion.div>
            ))}
            </div>
          </div>
        </div>

        {/* More Than Miles - Edge-to-edge section */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 max-w-[3000px] mx-auto">
          {/* Mobile: image then text */}
          <div className="lg:hidden flex flex-col">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              className="min-h-[55vh] relative overflow-hidden bg-gray-900 aspect-[3/4]"
            >
              <img
                src="/images/rewards/reward5.jpg"
                alt="Benefits"
                className="absolute inset-0 w-full h-full object-cover object-center"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = e.target.parentElement?.querySelector('[data-placeholder]');
                  if (placeholder) placeholder.classList.remove('hidden');
                }}
              />
              <div data-placeholder className="absolute inset-0 flex items-center justify-center hidden">
                <span className="text-white/30 text-xs font-normal text-center px-4">Add image to public/images/rewards/reward5.jpg</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              className="flex items-center justify-center p-8 sm:p-12 min-h-[40vh] bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900"
            >
              <div className="max-w-md">
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">More Than Miles</h2>
                <p className="text-white/60 font-light leading-relaxed text-base sm:text-lg">
                  No blackout dates. No expiration. Just flexible rewards that work for you.
                </p>
                <a
                  href="/search"
                  className="inline-block mt-6 border border-white/40 text-white px-6 py-2.5 text-sm font-normal hover:bg-white hover:text-black transition-colors duration-200"
                >
                  Start Earning
                </a>
              </div>
            </motion.div>
          </div>
          {/* Desktop: text left, image right - edge to edge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -50px 0px' }}
            className="hidden lg:flex items-center justify-center p-8 sm:p-12 lg:p-16 min-h-[70vh] bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900"
          >
            <div className="max-w-md">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-4">More Than Miles</h2>
              <p className="text-white/60 font-light leading-relaxed text-base sm:text-lg">
                No blackout dates. No expiration. Just flexible rewards that work for you.
              </p>
              <a
                href="/search"
                className="inline-block mt-6 border border-white/40 text-white px-6 py-2.5 text-sm font-normal hover:bg-white hover:text-black transition-colors duration-200"
              >
                Start Earning
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -50px 0px' }}
            className="hidden lg:block min-h-[70vh] max-h-[1500px] max-w-[1500px] relative overflow-hidden bg-gray-900"
          >
            <img
              src="/images/rewards/reward5.jpg"
              alt="Benefits"
              className="absolute inset-0 w-full h-full object-cover object-center"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.parentElement?.querySelector('[data-placeholder]');
                if (placeholder) placeholder.classList.remove('hidden');
              }}
            />
            <div data-placeholder className="absolute inset-0 flex items-center justify-center hidden">
              <span className="text-white/30 text-xs font-normal text-center px-4">Add image to public/images/rewards/reward5.jpg</span>
            </div>
          </motion.div>
        </div>

        {/* Fine Print */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-white/40 text-xs font-normal"
          >
            *Redemption values are estimates. Actual costs may vary based on airline inventory and pricing.
          </motion.p>
        </div>
      </div>

      <GiftCardRedeemModal
        open={redeemModalOpen}
        onClose={() => setRedeemModalOpen(false)}
        balance={milesBalance}
        isBalanceLoading={isLoading}
        userEmail={user?.email || ''}
        initialMerchant={redeemPrefill.merchant}
        initialMiles={redeemPrefill.miles}
        signedOut={!user}
      />
    </div>
  );
}
